import {ViewField} from "@/components/ui/forms/ViewField";

import React, {useEffect, useRef, useState} from "react";

import {useLevelOrder} from "@hooks/LevelOrder";
import {useScoreValue} from "@hooks/ScoreValue";
import {
  clearView,
  viewCertificationSession,
  viewProductSession,
  viewScenarioSession,
} from "@redux/slices/sessionSlices/viewSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {ControlButton} from "@ui/buttons/ControlButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {BorderArticle} from "@ui/general/BorderArticle";
import {Spinner} from "@ui/general/Spinner";
import moment from "moment/min/moment-with-locales";
import {CSVLink} from "react-csv";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useParams} from "react-router-dom";
import generatePDF, {Margin, Resolution} from "react-to-pdf";

import styles from "./styles.module.scss";

export const SessionViewPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {t} = useTranslation();
  const {id} = useParams();

  const {getScoreEnding} = useScoreValue();
  const {currentLevel} = useLevelOrder();
  const {isMobile} = useSelector((state) => state.common.settings);
  const {sessions} = useSelector((state) => state.common.viewTabs);
  const {current, status} = useSelector((state) => state.session.view);
  const [csvScenarioData, setCsvScenarioData] = useState([]);
  const [csvProductData, setCsvProductData] = useState([]);
  const [csvCertificationData, setCsvCertificationData] = useState([]);
  const sessionRef = useRef();

  const sessionName =
    sessions === "scenario"
      ? current?.scenario
      : sessions === "certification"
        ? current?.certification
        : current?.product;

  const scenarioMessages =
    current?.messages &&
    current?.messages
      ?.map((message) => {
        return `**REP:** ${message?.request}. **DOC:** ${message?.response}`;
      })
      .join(" / ");

  const productMessages =
    current?.answers &&
    current?.answers
      ?.map((message) => {
        return `**DOC:** ${message?.question}. **REP:** ${message?.answer}`;
      })
      .join(" / ");

  const pdfOptions = {
    filename: `${sessionName ? sessionName.replace(/ /g, "_") : ""}_${moment(
      current?.created_at,
    ).format(`DD_MM_YYYY`)}.pdf`,
    method: "save",
    resolution: Resolution.MEDIUM, // 3
    page: {
      margin: Margin.MEDIUM, // 10мм
      format: "A4",
      orientation: "portrait",
    },
  };

  const getTargetElement = () => document.getElementById("pdf-content");

  useEffect(() => {
    if (sessions === "scenario") {
      dispatch(viewScenarioSession({id}));
    } else if (sessions === "product") {
      dispatch(viewProductSession({id}));
    } else if (sessions === "certification") {
      dispatch(viewCertificationSession({id}));
    }

    return () => {
      dispatch(clearView());
    };
  }, [dispatch, sessions, id]);

  useEffect(() => {
    if (sessionRef.current && isMobile) {
      sessionRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [location]);

  useEffect(() => {
    if (!current) return;

    if (sessions === "scenario") {
        setCsvProductData(null);
        setCsvScenarioData([
          [
            t("table.created"),
            t("table.scenario"),
            t("table.user"),
            t("table.products"),
            t("table.level"),
            t("table.result"),
            t("table.feedback"),
            t("table.dialog"),
          ],
          [
            moment(current?.created_at).format(`DD-MM-YYYY HH:mm:ss`),
            current?.scenario,
            current?.user,
            current?.scenario_products.map(
              (product, index) =>
                product?.name +
                (index !== current?.scenario_products.length - 1 ? ", " : ""),
            ),
            currentLevel(current?.level),
            `${current?.successful ? t("table.certified") : t("table.not_certified")} / ${Math.round(Number(current?.feedback_score)) || 0}`,
            current?.feedback_json,
            scenarioMessages,
          ],
        ]);
      } else if (sessions === "product") {
        setCsvScenarioData(null);
        setCsvProductData([
          [
            t("table.created"),
            t("table.products"),
            t("table.user"),
            t("table.product_line"),
            t("table.level"),
            t("table.score"),
            t("table.feedback"),
            t("table.dialog"),
          ],
          [
            moment(current?.created_at).format(`DD-MM-YYYY HH:mm:ss`),
            current?.product,
            current?.user,
            current?.product_line,
            currentLevel(current?.level),
            `${current?.score} / ${current?.questions_count}`,
            current?.feedback_json,
            productMessages,
          ],
        ]);
      } else if (sessions === "certification") {
        setCsvScenarioData(null);
        setCsvCertificationData([
          [
            t("table.created"),
            t("table.scenario"),
            t("table.user"),
            t("table.date_time"),
            t("table.result"),
            t("table.feedback"),
            t("table.dialog"),
          ],
          [
            moment(current?.created_at).format(`DD-MM-YYYY HH:mm:ss`),
            current?.scenario,
            current?.user,
            current?.date_time,
            `${current?.certified ? t("table.certified") : t("table.not_certified")} / ${Math.round(Number(current?.feedback_score)) || 0}`,
            current?.feedback_json,
            scenarioMessages,
          ],
        ]);
      }
  }, [current]);

  return (
    <div
      ref={sessionRef}
      className={styles.sessionWrapper}
    >
      <BorderArticle>
        <div className={styles.session}>
          <BackButton />

          {status === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <>
              <div className={styles.sessionGenerateBtns}>
                <ControlButton
                  color={"var(--green)"}
                  txt={t("button.generate_pdf")}
                  onClick={() => generatePDF(getTargetElement, pdfOptions)}
                />

                <CSVLink
                  data={
                    sessions === "scenario"
                      ? csvScenarioData
                      : sessions === "product"
                        ? csvProductData
                        : csvCertificationData
                  }
                  filename={`${
                    sessionName ? sessionName.replace(/ /g, "_") : ""
                  }_${moment(current?.created_at).format(`DD_MM_YYYY`)}.csv`}
                >
                  <ControlButton
                    color={"var(--violet)"}
                    txt={t("button.generate_csv")}
                  />
                </CSVLink>
              </div>

              <div
                id={"pdf-content"}
                className={styles.sessionContent}
              >
                <div className={styles.sessionContentHead}>
                  <h2>
                    {moment(current?.created_at).format(`DD-MM-YYYY`)}{" "}
                    {moment(current?.created_at).format(`HH:mm:ss`)}
                  </h2>
                </div>

                <ul className={styles.sessionContentBody}>
                  {sessions === "scenario" || sessions === "certification" ? (
                    <ViewField
                      label={t("table.scenario")}
                      value={current?.scenario}
                    />
                  ) : sessions === "product" ? (
                    <ViewField
                      label={t("table.product")}
                      value={current?.product}
                    />
                  ) : null}

                  <ViewField
                    label={t("table.user")}
                    value={current?.user}
                  />

                  {sessions === "scenario" ? (
                    <ViewField
                      label={t("table.products")}
                      customContent={
                        <RootDesc>
                          <span className={"block p-[4rem_8rem]"}>
                            {current?.scenario_products?.length
                              ? current.scenario_products
                                  .map((p) => p?.name)
                                  .filter(Boolean)
                                  .join(", ")
                              : "-"}
                          </span>
                        </RootDesc>
                      }
                    />
                  ) : sessions === "product" ? (
                    <ViewField
                      label={t("table.product_line")}
                      value={current?.product_line}
                    />
                  ) : sessions === "certification" ? (
                    <ViewField
                      label={t("table.date_time")}
                      value={moment(current?.created_at).format(
                        `DD-MM-YYYY HH:mm:ss`,
                      )}
                    />
                  ) : null}

                  {sessions !== "certification" && (
                    <ViewField
                      label={t("table.level")}
                      value={currentLevel(current?.level)}
                    />
                  )}

                  {sessions === "scenario" ? (
                    <ViewField
                      label={t("table.result")}
                      value={`${current?.successful ? t("table.certified") : t("table.not_certified")} / ${Math.round(Number(current?.feedback_score)) || 0}`}
                    />
                  ) : sessions === "product" ? (
                    <ViewField
                      label={t("table.score")}
                      value={`${current?.score || 0} / ${current?.questions_count || 0}`}
                    />
                  ) : sessions === "certification" ? (
                    <ViewField
                      label={t("table.result")}
                      value={`${current?.certified ? t("table.certified") : t("table.not_certified")} / ${Math.round(Number(current?.feedback_score)) || 0}`}
                    />
                  ) : null}

                  {current?.feedback_json ? (
                    <ViewField
                      label={t("input.feedback")}
                      customContent={
                        <div
                          className={`${styles.sessionJson} ${sessions === "product" ? styles.productJson : ""}`}
                        >
                          {current?.feedback_json.map((item, index, items) => {
                            const isLast = index === items.length - 1;
                            const isFirst = index === 0;

                            return (
                              <div key={item.header}>
                                <article>
                                  {isLast ? (
                                    <strong>
                                      {t("popup.feedback.summary_title")}
                                    </strong>
                                  ) : item.header !== "" ? (
                                    <strong>{item.header}</strong>
                                  ) : null}

                                  {!isLast && (
                                    <>
                                      {item.score !== "" &&
                                      sessions !== "product" ? (
                                        <b>
                                          {item.score || 0}
                                          {getScoreEnding(item.score || 0)}
                                        </b>
                                      ) : (
                                        <b
                                          style={
                                            isFirst ? {fontSize: "24rem"} : {}
                                          }
                                        >
                                          {item.score || ""}
                                        </b>
                                      )}
                                    </>
                                  )}
                                </article>

                                {item.feedback !== "" && (
                                  <span>{item.feedback}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      }
                    />
                  ) : null}

                  <ViewField
                    label={t("input.dialog")}
                    customContent={
                      sessions === "scenario" ||
                      sessions === "certification" ? (
                        <div className={styles.sessionMessages}>
                          {current?.messages &&
                            current?.messages.map((message) => (
                              <div
                                className={styles.sessionMessage}
                                key={message?.id}
                              >
                                <RootDesc>
                                  <div className={"flex flex-col"}>
                                    <span>
                                      <span className={"underline"}>
                                        {t("page.session.dialog.rep")}
                                      </span>{" "}
                                      {message?.request}
                                    </span>
                                    <span>
                                      <span className={"underline"}>
                                        {t("page.session.dialog.doc")}
                                      </span>{" "}
                                      {message?.response}
                                    </span>
                                  </div>
                                </RootDesc>
                              </div>
                            ))}
                        </div>
                      ) : sessions === "product" ? (
                        <div className={styles.sessionMessages}>
                          {current?.answers &&
                            current?.answers.map((message) => (
                              <div
                                className={styles.sessionMessage}
                                key={message?.id}
                              >
                                <RootDesc>
                                  <div className={"flex flex-col"}>
                                    <span>
                                      <span className={"underline"}>
                                        {t("page.session.dialog.doc")}
                                      </span>{" "}
                                      {message?.question}
                                    </span>
                                    <span>
                                      <span className={"underline"}>
                                        {t("page.session.dialog.rep")}
                                      </span>{" "}
                                      {message?.answer}
                                    </span>
                                  </div>
                                </RootDesc>
                              </div>
                            ))}
                        </div>
                      ) : null
                    }
                  />
                </ul>
              </div>
            </>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
